import {VueController} from '@/service/support/vuex-controller';
import vaultService from '@/service/vault-service';

export default VueController("PdfSignWorkbench", {
    data() {
        return {
            loading: true,
            errorMessage: null,
            // Simulation mode data
            vaultId: null,
            fileId: null,
            // Sign mode data
            signatureRequest: null,
            signatureRequestId: null,
            token: null,
            recipientEmail: '',
            submitting: false,
            completed: false,
            // Shared data
            vault: null,
            file: null,
            pdfUrl: null,
            totalFields: 0,
            currentFieldIndex: 0,
            guidedSigningReady: false,
            allFieldsReviewed: false,
            navigateNextFieldFn: null,
            navigatePreviousFieldFn: null,
            showResults: false,
            simulationResults: null,
            participants: [],
            portalMembers: [],
            selectedParticipant: null,
            requiredFieldsList: [],
            signatureFieldsList: [],
            allFieldsData: [],
            nextButtonText: 'Start'
        };
    },
    state: [
        "organization",
        "user",
        "cognito"
    ],
    computed: {
        isSignMode() {
            return !!this.$route.params.signatureRequestId;
        },
        isSimulationMode() {
            return !!this.$route.params.vaultId && !!this.$route.params.fileId;
        },
        computedFieldAssignments() {
            if (!this.signatureRequest) return null;
            
            console.log('🧮 computedFieldAssignments: Recalculating for request', this.signatureRequest.id);
            
            // Start with top-level assignments if they exist
            const assignments = { ...(this.signatureRequest.field_assignments || {}) };
            
            // Handle both unmarshalled array and DynamoDB format { L: [...] }
            let recipientsList = this.signatureRequest.recipients;
            if (recipientsList && recipientsList.L) {
                recipientsList = recipientsList.L.map(item => item.M || item);
            }
            
            if (recipientsList && Array.isArray(recipientsList)) {
                console.log('🧮 Processing', recipientsList.length, 'recipients');
                recipientsList.forEach((recipient, idx) => {
                    // Handle unmarshalled vs DynamoDB recipient object
                    const email = recipient.email && recipient.email.S ? recipient.email.S : recipient.email;
                    const name = recipient.name && recipient.name.S ? recipient.name.S : (recipient.name || email);
                    const color = recipient.color && recipient.color.S ? recipient.color.S : recipient.color;
                    
                    // Handle assigned_fields
                    let assignedFields = recipient.assigned_fields;
                    if (assignedFields && assignedFields.L) {
                        assignedFields = assignedFields.L;
                    }
                    
                    if (assignedFields && Array.isArray(assignedFields)) {
                        assignedFields.forEach(fieldItem => {
                            // Handle DynamoDB JSON format (e.g. { S: "Name" }) or plain string
                            const fieldName = (fieldItem && typeof fieldItem === 'object' && fieldItem.S) ? fieldItem.S : fieldItem;
                            
                            if (typeof fieldName === 'string') {
                                assignments[fieldName] = {
                                    participantEmail: email,
                                    participantName: name,
                                    participantColor: color,
                                    isRequired: true
                                };
                                console.log(`🧮 Assigned "${fieldName}" to ${email}`);
                            }
                        });
                    }
                });
            } else {
                console.warn('🧮 recipients list is missing or not an array:', recipientsList);
            }
            
            const result = Object.keys(assignments).length > 0 ? assignments : null;
            console.log('🧮 computedFieldAssignments Result:', result ? Object.keys(result).length + ' assignments' : 'null');
            return result;
        },
        participantOptions() {
            const options = [
                {
                    value: 'all',
                    label: 'All Participants (View All Fields)',
                    color: '#757575',
                    order: ''
                },
                {
                    value: 'any_guest',
                    label: 'Any Guest',
                    color: '#00ACC1',
                    isAnyGuest: true,
                    order: '👥'
                }
            ];
            
            // Use actual portal members as participant options
            if (this.portalMembers.length > 0) {
                this.portalMembers.forEach((member, index) => {
                    let label = member.name;
                    
                    // Handle organization display
                    if (member.isOrganization) {
                        label = member.name; // Organization name
                    } else if (member.email && member.name !== member.email) {
                        label = member.name + ' (' + member.email + ')';
                    } else {
                        label = member.email;
                    }
                    
                    // Add "You" indicator for current user
                    if (member.isSelf) {
                        label = label + ' - You';
                    }
                    
                    // Add Organization indicator for non-guests
                    if (member.userType && member.userType !== 'guest' && !member.isSelf) {
                         // If it's not the current user but is an org member
                         label = label + ' (Organization)';
                    } else if (member.isSelf && member.userType && member.userType !== 'guest') {
                         // If it is the current user and they are an org member
                         label = label + ' (Organization)';
                    }
                    
                    options.push({
                        value: member.email, // Use email as ID
                        label: label,
                        email: member.email,
                        name: member.name,
                        userType: member.userType,
                        color: member.color || '#1976D2',
                        isSelf: member.isSelf,
                        isOrganization: member.isOrganization || false,
                        order: member.isOrganization ? '🏢' : (index + 1)
                    });
                });
            } else if (this.participants.length > 0) {
                // Fallback to XFDF participants if no portal members loaded
                this.participants.forEach((p, index) => {
                    let label = p.email || 'Unknown';
                    if (p.name && p.name !== 'Signer (Not Assigned)') {
                        label = p.name + ' (' + p.email + ')';
                    }
                    
                    options.push({
                        value: p.email || p.id,
                        label: label,
                        email: p.email,
                        color: p.color || '#1976D2',
                        order: index + 1
                    });
                });
            }
            
            console.log('Generated participant options:', options);
            return options;
        },
        progressPercentage() {
            if (this.totalFields === 0) return 0;
            return Math.round(((this.currentFieldIndex + 1) / this.totalFields) * 100);
        },
        hasManagePerms() {
            // Check if user has manage_perms permission on the vault
            if (!this.vault || !this.user) return false;
            
            let hasLocalPermissions = false;
            let hasOrgPermissions = false;
            
            if (this.vault && this.vault.permissions) {
                hasLocalPermissions = !!this.vault.permissions.find(perm => 
                    perm.manage_perms === true && 
                    perm.user_id === this.user.id && 
                    perm._deleted !== 1
                );
            }
            
            if (this.organization && this.vault && this.vault.org_id === this.organization.id) {
                if (this.cognito && this.cognito.attributes && this.cognito.attributes["custom:mutableUserType"] === "admin") {
                    hasOrgPermissions = true;
                } else if (!hasLocalPermissions && this.organization.permissions) {
                    hasOrgPermissions = !!this.organization.permissions.find(perm => 
                        perm.manage_perms === true && 
                        perm.user_id === this.user.id && 
                        perm._deleted !== 1
                    );
                }
            }
            
            return hasLocalPermissions || hasOrgPermissions;
        },
        portalMembersForAssignment() {
            // Format portal members for the assignment dropdown
            const options = [
                {
                    email: 'any_guest',
                    name: 'Any Guest',
                    label: 'Any Guest (any portal guest can sign)',
                    color: '#00ACC1',
                    isAnyGuest: true,
                    order: 0
                }
            ];
            
            if (this.portalMembers.length > 0) {
                this.portalMembers.forEach((member, index) => {
                    let label = member.name;
                    if (member.email && member.name !== member.email) {
                        label = member.name + ' (' + member.email + ')';
                    } else {
                        label = member.email;
                    }
                    
                    options.push({
                        email: member.email,
                        name: member.name,
                        label: label,
                        color: member.color || '#1976D2',
                        isSelf: member.isSelf,
                        isOrganization: member.isOrganization,
                        order: index + 1
                    });
                });
            }
            
            return options;
        },
        activeParticipantFields() {
            // Filter by active participant for BOTH modes
            if (!this.allFieldsData) {
                console.log('💡 activeParticipantFields: No allFieldsData, returning []');
                return [];
            }
            if (!this.selectedParticipant || this.selectedParticipant === 'all') {
                console.log('💡 activeParticipantFields: No participant filter, returning all', this.requiredFieldsList.length, 'fields');
                return this.requiredFieldsList;
            }
            const filtered = this.requiredFieldsList.filter(f => 
                this.canUserAccessField(f, this.selectedParticipant)
            );
            console.log(`💡 activeParticipantFields: Filtered ${this.requiredFieldsList.length} fields → ${filtered.length} for participant "${this.selectedParticipant}"`);
            return filtered;
        },
        activeParticipantProgress() {
            // Use requiredFieldsList directly for more accurate progress tracking
            const fields = this.requiredFieldsList || [];
            
            // Filter by selected participant if applicable (BOTH modes)
            let relevantFields = fields;
            if (this.selectedParticipant && this.selectedParticipant !== 'all') {
                relevantFields = fields.filter(f => 
                    this.canUserAccessField(f, this.selectedParticipant)
                );
            }
            
            const total = relevantFields.length;
            const completed = relevantFields.filter(f => f.filled).length;
            const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
            
            console.log(`💡 activeParticipantProgress: ${completed}/${total} (${percentage}%) for participant "${this.selectedParticipant}"`);
            
            return {
                total,
                completed,
                percentage
            };
        }
    },
    mounted() {
        if (this.$route.params.signatureRequestId) {
            // Sign mode
            this.signatureRequestId = this.$route.params.signatureRequestId;
            this.token = this.$route.params.token;
            
            if (!this.signatureRequestId || !this.token) {
                this.errorMessage = 'Invalid signature request link';
                this.loading = false;
                return;
            }
        } else {
            // Simulation mode
            this.vaultId = this.$route.params.vaultId;
            this.fileId = this.$route.params.fileId;
            
            if (!this.vaultId || !this.fileId) {
                this.errorMessage = 'Invalid simulation parameters';
                this.loading = false;
                return;
            }
        }
        
        // Use unified loading for both modes
        this.loadDocument();
    },
    methods: {
        canUserAccessField(field, userIdentifier) {
            // CRITICAL: Check for direct match FIRST. If the email matches, the user has access.
            // Do not let "default names" like "Signer (Not Assigned)" cause a false negative.
            if (field.participantEmail === userIdentifier || field.participantId === userIdentifier) {
                // console.log(`  ✅ ${field.fieldName} ALLOWED: direct email/ID match`);
                return true;
            }
            
            // Handle "Not Assigned" fields differently based on mode
            const isUnassigned = !field.participantEmail || 
                field.participantEmail === 'signer@simulation.local' || 
                field.participantEmail === 'unassigned' ||
                field.participantName === 'Signer (Not Assigned)' ||
                field.participantEmail === 'not_assigned';
            
            if (isUnassigned) {
                // console.log(`  ⚠️ ${field.fieldName} treated as UNASSIGNED`);
                // In SIMULATION mode: unassigned fields should only show when viewing "all"
                if (this.isSimulationMode) {
                    return userIdentifier === 'all';
                }
                
                // In SIGN mode: handle unassigned fields
                if (this.isSignMode) {
                    // Unassigned SIGNATURE fields are "Not Required" - exclude them
                    if (field.fieldType === 'signature') {
                        // console.log(`  ❌ ${field.fieldName} REJECTED: unassigned signature in sign mode`);
                        return false;
                    }
                    
                    // Unassigned but required NON-SIGNATURE fields should be shown to the recipient
                    // This handles required form fields that weren't explicitly assigned
                    if (field.required) {
                        // console.log(`  ✅ ${field.fieldName} ALLOWED: unassigned but required non-signature field`);
                        return true;
                    }
                    
                    // Non-required, unassigned fields - don't show
                    // console.log(`  ❌ ${field.fieldName} REJECTED: unassigned and not required`);
                    return false;
                }
                
                // Default: don't show unassigned fields
                return false;
            }
            
            // Check if field is assigned to "Any Guest"
            if (field.participantEmail === 'any_guest' || field.isAnyGuest === 'true' || field.isAnyGuest === true) {
                console.log(`  🔍 ${field.fieldName} is assigned to "Any Guest"`);
                
                // In Simulation mode: Check if we're viewing as "any_guest"
                if (this.isSimulationMode) {
                    if (userIdentifier === 'any_guest') {
                        console.log(`  ✅ ${field.fieldName} ALLOWED: simulation mode viewing as "Any Guest"`);
                        return true;
                    }
                    // Any portal guest can access when viewing "all"
                    if (userIdentifier === 'all') {
                        console.log(`  ✅ ${field.fieldName} ALLOWED: simulation mode viewing "all"`);
                        return true;
                    }
                    // Check if the selected participant is a guest (not org member/admin)
                    const selectedParticipantData = this.portalMembers.find(m => m.email === userIdentifier);
                    if (selectedParticipantData && selectedParticipantData.type === 'guest') {
                        console.log(`  ✅ ${field.fieldName} ALLOWED: selected participant is a guest`);
                        return true;
                    }
                    console.log(`  ❌ ${field.fieldName} REJECTED: participant is not a guest`);
                    return false;
                }
                
                // In Sign mode: Any guest user can access "any_guest" fields
                if (this.isSignMode) {
                    // Check if the user is a guest (not org member/admin)
                    const isOrgMemberOrAdmin = this.user && (this.user.memberOf || this.user.administratorOf);
                    if (!isOrgMemberOrAdmin) {
                        console.log(`  ✅ ${field.fieldName} ALLOWED: user is a guest, field is "Any Guest"`);
                        return true;
                    }
                    console.log(`  ❌ ${field.fieldName} REJECTED: "Any Guest" field, but user is org member/admin`);
                    return false;
                }
            }
            
            // Check if field is assigned to an organization
            if (field.participantEmail && field.participantEmail.startsWith('org:')) {
                const orgId = field.participantEmail.substring(4);
                
                // In Simulation mode: Check if we're viewing as the organization or as a member
                if (this.isSimulationMode) {
                    // If checking against organization ID directly (simulation mode selector)
                    if (userIdentifier && userIdentifier.startsWith('org:')) {
                        return field.participantEmail === userIdentifier;
                    }
                    
                    // Check if the selected participant user is a member/admin of this org
                    if (this.user && (this.user.memberOf === orgId || this.user.administratorOf === orgId)) {
                        return true;
                    }
                }
                
                // In Sign mode: Check if the logged-in/recipient user is a member/admin of this org
                if (this.isSignMode) {
                    // Sign mode for guests: they're NOT part of the organization
                    // Only actual organization members (logged-in users) should see org fields
                    if (this.user && (this.user.memberOf === orgId || this.user.administratorOf === orgId)) {
                        // Check if the current recipient (userIdentifier) matches the logged-in user
                        if (this.user.email === userIdentifier) {
                            return true;
                        }
                    }
                    // Guest recipients (no user object or not a member) cannot access org fields
                    console.log(`  ❌ ${field.fieldName} REJECTED: org field, but user is not a member`);
                    return false;
                }
            }
            
            console.log(`  ❌ ${field.fieldName} REJECTED: no matching criteria (participantEmail: ${field.participantEmail}, participantId: ${field.participantId})`);
            return false;
        },
        // ========== UNIFIED LOADING METHOD ==========
        async loadDocument() {
            try {
                this.loading = true;
                
                // If sign mode, load signature request metadata and extract vault/file IDs
                if (this.isSignMode) {
                    const response = await vaultService.query('getSignatureRequest', {
                        signature_request_id: this.signatureRequestId,
                        token: this.token
                    }, undefined, {vuexCache: false});
                    
                    if (!response) {
                        throw new Error('Signature request not found or has expired');
                    }
                    
                    console.log('📝 RAW SIGNATURE REQUEST:', JSON.parse(JSON.stringify(response)));
                    
                    this.signatureRequest = response;
                    // Use current_recipient_email from backend (determined by token)
                    this.recipientEmail = response.current_recipient_email || response.recipient_email;
                    
                    if (response.file_deleted) {
                        this.errorMessage = 'This signature request cannot be completed because the file has been deleted. Please contact the sender.';
                        this.loading = false;
                        return;
                    }
                    
                    // Check if THIS recipient has already completed (not just overall status)
                    const currentRecipient = response.recipients && response.recipients.length > 0
                        ? response.recipients.find(r => r.email === this.recipientEmail)
                        : null;
                    
                    if (currentRecipient && currentRecipient.status === 'completed') {
                        this.errorMessage = 'You have already completed this signature request';
                        this.loading = false;
                        return;
                    }
                    
                    if (response.status === 'completed' && !currentRecipient) {
                        // Fallback for old single-recipient format
                        this.errorMessage = 'This signature request has already been completed';
                        this.loading = false;
                        return;
                    }
                    
                    if (response.status === 'cancelled') {
                        this.errorMessage = 'This signature request has been cancelled';
                        this.loading = false;
                        return;
                    }
                    
                    if (response.expires_at && new Date(response.expires_at) < new Date()) {
                        this.errorMessage = 'This signature request has expired';
                        this.loading = false;
                        return;
                    }
                    
                    // Extract vault and file IDs for unified loading
                    this.vaultId = response.portal_id;
                    this.fileId = response.file_id;
                }
                
                // NOW use the exact same loading logic for both modes
                // Load vault information
                const vault = await vaultService.query('getVaultById', {
                    vault_id: this.vaultId,
                    loadAuditHistory: false
                }, undefined, {vuexCache: false});
                
                if (!vault) {
                    throw new Error('Portal not found');
                }
                
                this.vault = vault;
                
                // Check permissions (simulation mode only)
                if (this.isSimulationMode && !this.hasManagePerms) {
                    throw new Error('You do not have permission to access simulation mode for this portal');
                }
                
                // Load portal members/guests (simulation mode only)
                if (this.isSimulationMode) {
                    await this.loadPortalMembers();
                }
                
                // Load file information - SAME for both modes
                const files = await vaultService.query('getFilesByVaultId', {
                    vault_id: this.vaultId,
                    show_deleted: false
                }, undefined, {vuexCache: false});
                
                const file = files ? files.find(f => f.id === this.fileId) : null;
                
                if (!file) {
                    throw new Error('File not found');
                }
                
                if (file.extension !== 'pdf') {
                    throw new Error('Only PDF files can be opened in this mode');
                }
                
                this.file = file;
                
                // Get signed URL for the PDF - SAME for both modes
                this.pdfUrl = await vaultService.getSignedFileUrl(this.file, this.vault, true);
                
                this.loading = false;
                
                // RACE CONDITION FIX:
                // If WebViewer is already ready (guidedSigningReady=true), it means onGuidedSigningReady
                // fired BEFORE we finished loading signatureRequest data.
                // We must re-run extraction now that we have the data.
                if (this.guidedSigningReady) {
                    console.log('🔄 Signature request loaded after WebViewer - re-running field extraction');
                    this.extractFieldInformation();
                }
            } catch (error) {
                console.error('Error loading document:', error);
                this.errorMessage = error.message || 'Failed to load document';
                this.loading = false;
            }
        },
        handleNextButton() {
            if (this.nextButtonText === 'Completed') {
                // All fields were already completed - return to portal
                this.returnToPortal();
            } else if (this.nextButtonText === 'Finish') {
                if (this.isSignMode) {
                    this.completeSignature();
                } else if (this.isSimulationMode) {
                    // In simulation mode, "Finish" just navigates (user can manually show results)
                    this.navigateToNextField();
                }
            } else {
                this.navigateToNextField();
            }
        },
        returnToPortal() {
            // Show a friendly message
            this.notification('info', 'This document has already been completed.');
            
            // Return to the portal view after a brief moment
            setTimeout(() => {
                if (this.vault && this.vault.id) {
                    this.$router.push({
                        name: 'Portal',
                        params: {
                            orgid: this.vault.org_id,
                            vaultid: this.vault.id,
                            folderid: '0'
                        }
                    });
                } else {
                    // Fallback to home/dashboard
                    this.$router.push('/');
                }
            }, 1000);
        },
        returnToPortalFromError() {
            // Return to portal from error state (no notification needed)
            if (this.vault && this.vault.id) {
                this.$router.push({
                    name: 'Portal',
                    params: {
                        orgid: this.vault.org_id,
                        vaultid: this.vault.id,
                        folderid: '0'
                    }
                });
            } else {
                // Fallback to home/dashboard
                this.$router.push('/');
            }
        },
        async completeSignature() {
            if (!this.$refs.pdfViewer) {
                this.notification('error', 'PDF viewer not ready');
                return;
            }
            
            try {
                this.submitting = true;
                
                const isValid = this.$refs.pdfViewer.validateRequiredFields();
                if (!isValid) {
                    this.notification('error', 'Please complete all required fields before submitting');
                    this.submitting = false;
                    return;
                }
                
                await this.$refs.pdfViewer.completeSignature();
            } catch (error) {
                console.error('Error completing signature:', error);
                this.notification('error', 'Failed to complete signature. Please try again.');
                this.submitting = false;
            }
        },
        async onSignatureCompleted(data) {
            try {
                const response = await vaultService.mutation('completeSignature', {
                    signature_request_id: data.signatureRequestId,
                    completed_xfdf: data.xfdfString,
                    token: this.token
                });
                
                if (response) {
                    this.completed = true;
                    this.notification('success', 'Signature completed successfully!');
                } else {
                    throw new Error('Failed to complete signature');
                }
            } catch (error) {
                console.error('Error submitting signature:', error);
                this.notification('error', 'Failed to submit signature. Please try again.');
            } finally {
                this.submitting = false;
            }
        },
        updateNextButtonText(text) {
            this.nextButtonText = text;
        },
        
        // ========== SIMULATION MODE METHODS ==========
        async loadPortalMembers() {
            // Load users with explicit access to this portal
            this.portalMembers = [];
            
            if (!this.vault || !this.vault.permissions) {
                return;
            }
            
            // First, add the organization as a participant option
            if (this.organization && this.organization.companyName) {
                this.portalMembers.push({
                    id: `org:${this.organization.id}`,
                    email: `org:${this.organization.id}`,
                    name: this.organization.companyName,
                    userType: 'organization',
                    color: '#1976D2', // Primary blue for organization
                    isSelf: false,
                    isOrganization: true
                });
            }
            
            // Always add current user (yourself) first as a participant option
            if (this.user && this.user.email) {
                const currentUserType = this.cognito && this.cognito.attributes && this.cognito.attributes["custom:mutableUserType"] 
                    ? this.cognito.attributes["custom:mutableUserType"] 
                    : 'member';
                
                this.portalMembers.push({
                    id: this.user.id,
                    email: this.user.email,
                    name: this.user.displayName || this.user.name || this.user.email,
                    userType: currentUserType,
                    color: this.getColorForUser(this.user.email),
                    isSelf: true
                });
            }
            
            // Get users from vault permissions (explicitly added members)
            // NOTE: We show ALL members here so you can simulate as anyone (pending guests, other admins, etc.)
            let activePermissions = [];
            if (this.vault && this.vault.permissions) {
                 activePermissions = this.vault.permissions.filter(perm => {
                    // Skip deleted permissions
                    if (perm._deleted === 1) return false;
                    
                    // Skip if no user_id
                    if (!perm.user_id) return false;
                    
                    // Skip if this is the current user (by ID or email)
                    if (this.user) {
                        if (perm.user_id === this.user.id) {
                            console.log('Filtering out current user by ID:', perm.user_id);
                            return false;
                        }
                        if (perm.user_email === this.user.email) {
                            console.log('Filtering out current user by email:', perm.user_email);
                            return false;
                        }
                    }
                    
                    return true;
                });
            }
            
            console.log('activePermissions after filtering:', activePermissions);
            console.log('activePermissions details:', JSON.stringify(activePermissions, null, 2));
            
            // Load user details for each permission
            for (const perm of activePermissions) {
                try {
                    // For simulation mode, use permission data directly to ensure accuracy
                    // This avoids issues with getUserById returning incorrect/cached data
                    if (perm.user_email) {
                        this.portalMembers.push({
                            id: perm.user_id,
                            email: perm.user_email,
                            name: perm.user_name || perm.user_email,
                            userType: perm.user_type || 'guest',
                            color: this.getColorForUser(perm.user_email),
                            isSelf: false,
                            invitationAccepted: perm.invitation_accepted
                        });
                        console.log('Added portal member from permission:', perm.user_name, perm.user_email);
                    }
                } catch (error) {
                    console.warn('Error processing permission for:', perm.user_id, error);
                }
            }
            
            console.log('Loaded portal members:', this.portalMembers.length, '(including yourself)');
        },
        getColorForUser(email) {
            // Generate consistent color based on email
            const colors = ['#1976D2', '#388E3C', '#7B1FA2', '#F57C00', '#C2185B', '#00796B', '#5D4037', '#455A64'];
            const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
            return colors[hash % colors.length];
        },
        
        // ========== SHARED METHODS ==========
        onGuidedSigningReady(data) {
            console.log('📥 Guided signing ready event received. Mode:', this.isSignMode ? 'Sign' : 'Simulation');
            console.log('📥 Total fields:', data.totalFields);
            console.log('📥 Fields data:', data.fields);
            
            this.totalFields = data.totalFields;
            this.navigateNextFieldFn = data.navigateNext;
            this.navigatePreviousFieldFn = data.navigatePrevious;
            this.guidedSigningReady = true;
            
            // Populate fields list
            if (data.fields && data.fields.length > 0) {
                let allMappedFields = data.fields.map(field => ({
                    id: field.id,
                    fieldName: field.fieldName,
                    displayName: field.displayName || field.fieldName,
                    fieldType: field.fieldType || 'text',
                    pageNumber: field.pageNumber || 1,
                    participantId: field.participantId,
                    participantEmail: field.participantEmail,
                    participantName: field.participantName,
                    participantColor: field.participantColor || '#1976D2',
                    filled: field.filled || false,
                    subject: field.subject,
                    required: field.required
                }));

                // Ensure selectedParticipant is set for Sign Mode so we can filter immediately
                if (this.isSignMode && !this.selectedParticipant && this.recipientEmail) {
                    this.selectedParticipant = this.recipientEmail;
                }

                // FILTER HERE for Sign Mode
                if (this.isSignMode && this.selectedParticipant) {
                    const beforeCount = allMappedFields.length;
                    console.log(`🔍 onGuidedSigningReady: Filtering fields for ${this.selectedParticipant}`);
                    allMappedFields = allMappedFields.filter(field => {
                         return this.canUserAccessField(field, this.selectedParticipant);
                    });
                    console.log(`🎯 onGuidedSigningReady: Filtered fields from ${beforeCount} → ${allMappedFields.length}`);
                }

                this.requiredFieldsList = allMappedFields;
                
                console.log('✅ Controller populated requiredFieldsList with', this.requiredFieldsList.length, 'fields');
                
                this.signatureFieldsList = this.requiredFieldsList.filter(f => 
                    f.fieldType === 'signature' || f.fieldType === 'sig'
                );
                
                console.log('✅ Found', this.signatureFieldsList.length, 'signature fields');
            } else {
                console.warn('⚠️ No fields data received from WebViewer!');
            }
            
            // Extract detailed field information for BOTH modes
            // This filters to only required fields and handles field properties
            this.extractFieldInformation();
        },
        onFieldNavigated(data) {
            this.currentFieldIndex = data.index;
        },
        onAllFieldsReviewed() {
            this.allFieldsReviewed = true;
        },
        navigateToNextField() {
            if (this.navigateNextFieldFn) {
                this.navigateNextFieldFn();
            }
        },
        navigateToPreviousField() {
            if (this.navigatePreviousFieldFn) {
                this.navigatePreviousFieldFn();
            }
        },
        async showSimulationResults() {
            if (!this.$refs.pdfViewer) {
                this.notification('error', 'PDF viewer not ready');
                return;
            }
            
            try {
                // Get the simulation results (XFDF data)
                const results = await this.$refs.pdfViewer.getSimulationResults();
                
                if (results) {
                    this.simulationResults = results;
                    this.showResults = true;
                } else {
                    this.notification('error', 'Unable to retrieve simulation data');
                }
            } catch (error) {
                console.error('Error retrieving simulation results:', error);
                this.notification('error', 'Failed to retrieve simulation results');
            }
        },
        closeResults() {
            this.showResults = false;
            this.simulationResults = null;
        },
        exitSimulation() {
            // If we have vault info, try to return to the vault
            if (this.vault && this.vault.id) {
                this.$router.push({
                    name: 'Portal',
                    params: {
                        orgid: this.vault.org_id,
                        vaultid: this.vault.id,
                        folderid: '0'
                    }
                });
            } else {
                // Fallback to home/dashboard
                this.$router.push('/');
            }
        },
        async extractFieldInformation() {
            if (!this.$refs.pdfViewer) {
                console.warn('PDF Viewer ref not available');
                return;
            }
            
            // Wait a bit for the viewer to be fully ready
            await new Promise(resolve => setTimeout(resolve, 300));
            
            try {
                console.log('🎯 ===== CONTROLLER: extractFieldInformation START =====');
                console.log('🎯 Mode:', this.isSignMode ? 'SIGN' : 'SIMULATION');
                console.log('🎯 Recipient email (sign mode):', this.recipientEmail);
                
                const fieldsData = await this.$refs.pdfViewer.getFieldsInformation();
                
                if (!fieldsData) {
                    console.warn('🎯 No fields data returned from WebViewer');
                    return;
                }
                
                this.allFieldsData = fieldsData.fields || [];
                this.participants = fieldsData.participants || [];
                
                console.log('🎯 Received from WebViewer:', this.allFieldsData.length, 'fields,', this.participants.length, 'participants');

                // CRITICAL FIX: Force assignment from signature request recipients if in Sign Mode
                // This ensures we don't rely solely on WebViewer/XFDF for assignments
                if (this.isSignMode && this.signatureRequest && this.signatureRequest.recipients) {
                    console.log('🎯 Applying assignments from DB SignatureRequest object...');
                    
                    // Create a map of field assignments: fieldName -> recipient
                    const fieldMap = {};
                    
                    // Helper to process recipients (handle DynamoDB format if needed, though query returns clean JSON)
                    let recipientsList = this.signatureRequest.recipients;
                    // Basic check if it's raw DynamoDB (unlikely with AppSync but possible)
                    if (recipientsList.L) recipientsList = recipientsList.L;
                    
                    if (Array.isArray(recipientsList)) {
                        recipientsList.forEach(recipient => {
                            // Handle potential DynamoDB raw format or clean JSON
                            const rObj = recipient.M || recipient;
                            const email = rObj.email && rObj.email.S ? rObj.email.S : rObj.email;
                            const name = rObj.name && rObj.name.S ? rObj.name.S : rObj.name;
                            const color = rObj.color && rObj.color.S ? rObj.color.S : (rObj.color || '#1976D2');
                            
                            let assignedFields = rObj.assigned_fields;
                            if (assignedFields && assignedFields.L) assignedFields = assignedFields.L;
                            
                            if (assignedFields && Array.isArray(assignedFields)) {
                                assignedFields.forEach(fieldItem => {
                                    const fieldName = (fieldItem && typeof fieldItem === 'object' && fieldItem.S) ? fieldItem.S : fieldItem;
                                    if (typeof fieldName === 'string') {
                                        fieldMap[fieldName] = {
                                            email: email,
                                            name: name || email,
                                            color: color
                                        };
                                    }
                                });
                            }
                        });
                    }
                    
                    // Apply to fields
                    let appliedCount = 0;
                    this.allFieldsData.forEach(field => {
                        const assignment = fieldMap[field.fieldName];
                        if (assignment) {
                            // Only override if not already assigned (or to ensure correctness)
                            // We force override here because DB is the source of truth
                            field.participantId = assignment.email;
                            field.participantEmail = assignment.email;
                            field.participantName = assignment.name;
                            field.participantColor = assignment.color;
                            field.required = true; // Assigned fields are required for that user
                            appliedCount++;
                        }
                    });
                    
                    console.log(`🎯 Applied DB assignments to ${appliedCount} fields`);
                }

                console.log('🎯 Field assignments from WebViewer:');
                this.allFieldsData.forEach(f => {
                    console.log(`   ${f.fieldName}: ${f.participantEmail || 'unassigned'}`);
                });
                
                // Load saved assignments from storage
                const savedAssignments = this.loadSavedAssignments();
                
                // Apply saved assignments or auto-assign
                if (savedAssignments && savedAssignments.length > 0) {
                    // Apply saved assignments
                    this.allFieldsData.forEach(field => {
                        const savedAssignment = savedAssignments.find(a => 
                            a.fieldId === field.id || a.fieldName === field.fieldName
                        );
                        
                        if (savedAssignment) {
                            field.participantId = savedAssignment.participantEmail;
                            field.participantEmail = savedAssignment.participantEmail;
                            field.participantName = savedAssignment.participantName;
                            field.participantColor = savedAssignment.participantColor;
                            
                            // Update the annotation in the viewer
                            if (this.$refs.pdfViewer && this.$refs.pdfViewer.updateFieldParticipant) {
                                const participant = {
                                    email: savedAssignment.participantEmail,
                                    name: savedAssignment.participantName,
                                    color: savedAssignment.participantColor
                                };
                                this.$refs.pdfViewer.updateFieldParticipant(field.id, participant);
                            }
                        }
                    });
                } else if (this.portalMembers.length > 0) {
                    // Simulation mode: Auto-assign unassigned fields to portal members
                    let memberIndex = 0;
                    this.allFieldsData.forEach(field => {
                        // If field has no real assignment (using the default simulation email)
                        if (!field.participantEmail || field.participantEmail === 'signer@simulation.local') {
                            // Round-robin assign to portal members
                            const member = this.portalMembers[memberIndex % this.portalMembers.length];
                            field.participantId = member.email;
                            field.participantEmail = member.email;
                            field.participantName = member.name;
                            field.participantColor = member.color;
                            memberIndex++;
                        }
                    });
                }
                
                // Extract required fields (signatures + any marked as required)
                const allRequiredFields = this.allFieldsData.filter(field => {
                    // Must be marked as required or be a signature
                    return field.required || field.fieldType === 'signature';
                });
                
                // Deduplicate radio button groups (they have multiple widgets but should only show once)
                const seenFieldNames = new Set();
                this.requiredFieldsList = allRequiredFields.filter(field => {
                    // For radio buttons, only keep the first occurrence
                    if (field.fieldType === 'radio') {
                        if (seenFieldNames.has(field.fieldName)) {
                            return false; // Skip duplicate
                        }
                        seenFieldNames.add(field.fieldName);
                        
                        // Improve display name for radio groups
                        // Convert field name to readable format: "contact_method" -> "Contact Method"
                        field.displayName = field.fieldName
                            .split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ');
                        
                        console.log('📻 Radio button display name set:', field.fieldName, '->', field.displayName);
                    }
                    
                    return true;
                });
                
                // Initialize the 'filled' property from 'isFilled' for already-filled fields
                // This ensures pre-filled fields show as complete in the progress tracker
                this.requiredFieldsList.forEach(field => {
                    if (field.isFilled) {
                        // Use Vue.set to ensure reactivity
                        this.$set(field, 'filled', true);
                        console.log('✅ Initialized pre-filled field:', field.fieldName, '(', field.fieldType, ') - isFilled:', field.isFilled);
                    } else {
                        // Explicitly initialize as false if not filled
                        this.$set(field, 'filled', false);
                    }
                });

                // Set default participant FIRST before filtering
                if (!this.selectedParticipant) {
                    if (this.isSignMode) {
                        // Sign mode: Use the recipient's email
                        this.selectedParticipant = this.recipientEmail;
                        console.log('🎯 Sign mode: Set active participant to', this.selectedParticipant);
                    } else if (this.portalMembers.length > 0) {
                        // Simulation mode: Find yourself in the list and default to that
                        const selfMember = this.portalMembers.find(m => m.isSelf);
                        if (selfMember) {
                            this.selectedParticipant = selfMember.email; // Start with yourself
                        } else {
                            this.selectedParticipant = this.portalMembers[0].email; // Or first member
                        }
                        console.log('🎯 Simulation mode: Set active participant to', this.selectedParticipant);
                    } else {
                        this.selectedParticipant = 'all'; // Or show all fields
                        console.log('🎯 Set active participant to "all"');
                    }
                }

                // In Sign mode, filter requiredFieldsList to only show fields accessible to the recipient
                if (this.isSignMode && this.selectedParticipant) {
                    const beforeCount = this.requiredFieldsList.length;
                    console.log('🔍 Filtering fields for recipient:', this.selectedParticipant);
                    console.log('🔍 Fields before filtering:', this.requiredFieldsList.map(f => `${f.fieldName} (${f.participantEmail}) - filled:${f.filled || f.isFilled}`));
                    
                    this.requiredFieldsList = this.requiredFieldsList.filter(field => {
                        // Check if user can access this field
                        const canAccess = this.canUserAccessField(field, this.selectedParticipant);
                        if (!canAccess) {
                            console.log(`❌ Filtered OUT: ${field.fieldName} (${field.fieldType}) - participantEmail: ${field.participantEmail}`);
                        }
                        return canAccess;
                    });
                    console.log(`🎯 Sign mode: Filtered requiredFieldsList from ${beforeCount} → ${this.requiredFieldsList.length} fields for recipient`);
                    
                    // Log which fields are already filled
                    const preFilledFields = this.requiredFieldsList.filter(f => f.isFilled || f.filled);
                    if (preFilledFields.length > 0) {
                        console.log(`✅ Found ${preFilledFields.length} pre-filled fields:`, preFilledFields.map(f => f.fieldName));
                    }
                    
                    // Check if user has already completed all their fields
                    const userCompletedFields = preFilledFields.length;
                    const userTotalFields = this.requiredFieldsList.length;
                    if (userCompletedFields === userTotalFields && userTotalFields > 0) {
                        console.log('✅ All fields have been completed!');
                        this.notification('success', `All required fields have been completed! (${userCompletedFields}/${userTotalFields})`);
                    }
                }
                
                // Sort requiredFieldsList top-down based on Y position
                // It appears the Y coordinates are Top-Left based (0 at top), so we sort Ascending (smaller Y is higher on page)
                this.requiredFieldsList.sort((a, b) => {
                    if (a.pageNumber !== b.pageNumber) return a.pageNumber - b.pageNumber;
                    
                    // Use a tolerance of 10 units to group fields on roughly same line
                    if (Math.abs(a.y - b.y) > 10) return a.y - b.y; 
                    
                    return a.x - b.x; // Left-to-right
                });
                
                // Extract ONLY signature fields
                this.signatureFieldsList = this.allFieldsData.filter(field => 
                    field.fieldType === 'signature' && field.pdfFieldType === 'Sig'
                );
                
                // Log filtering results
                console.log('🎯 Active participant for filtering:', this.selectedParticipant);
                const filteredFields = this.requiredFieldsList.filter(f => 
                    f.participantEmail === this.selectedParticipant || 
                    f.participantId === this.selectedParticipant
                );
                console.log('🎯 Required fields BEFORE filtering:', this.requiredFieldsList.length);
                console.log('🎯 Required fields AFTER filtering by participant:', filteredFields.length);
                console.log('🎯 Filtered fields:', filteredFields.map(f => `${f.fieldName} (${f.participantEmail})`));
                console.log('🎯 ===== CONTROLLER: extractFieldInformation END =====');
                
                console.log('Extracted fields:', {
                    total: this.allFieldsData.length,
                    required: this.requiredFieldsList.length,
                    signatures: this.signatureFieldsList.length,
                    participants: this.participants.length
                });
                
                console.log('Field breakdown by type:', {
                    signatures: this.allFieldsData.filter(f => f.fieldType === 'signature').length,
                    text: this.allFieldsData.filter(f => f.fieldType === 'text').length,
                    checkbox: this.allFieldsData.filter(f => f.fieldType === 'checkbox').length,
                    radio: this.allFieldsData.filter(f => f.fieldType === 'radio').length,
                    choice: this.allFieldsData.filter(f => f.fieldType === 'choice').length
                });
                
                // Update all signature field annotations with participant data
                if (this.$refs.pdfViewer && this.$refs.pdfViewer.updateAllFieldParticipants) {
                    await this.$refs.pdfViewer.updateAllFieldParticipants(this.signatureFieldsList);
                }
                
                // Set required field information for styling
                // In Sign mode, only pass fields accessible to the current user
                // In Simulation mode, pass all fields
                if (this.$refs.pdfViewer && this.$refs.pdfViewer.setRequiredFields) {
                    let fieldsToSet = this.allFieldsData;
                    
                    if (this.isSignMode && this.selectedParticipant) {
                        // Filter to only fields accessible by the current recipient
                        fieldsToSet = this.allFieldsData.filter(field => 
                            this.canUserAccessField(field, this.selectedParticipant)
                        );
                        console.log(`🎯 Sign mode: Filtered ${this.allFieldsData.length} fields → ${fieldsToSet.length} for recipient`);
                    }
                    
                    this.$refs.pdfViewer.setRequiredFields(fieldsToSet);
                }
                
                // Apply participant filtering now that fields are assigned
                if (this.selectedParticipant && this.$refs.pdfViewer && this.$refs.pdfViewer.setActiveParticipant) {
                    this.$refs.pdfViewer.setActiveParticipant(this.selectedParticipant);
                }
                
            } catch (error) {
                console.error('Error extracting field information:', error);
            }
        },
        getFieldsByParticipant(participantId) {
            return this.allFieldsData.filter(field => 
                field.participantId === participantId || field.recipientIndex === participantId
            );
        },
        changeParticipant(participantId) {
            this.selectedParticipant = participantId;
            
            // Notify WebViewer of participant change for filtering
            if (this.$refs.pdfViewer && this.$refs.pdfViewer.setActiveParticipant) {
                this.$refs.pdfViewer.setActiveParticipant(participantId);
            }
        },
        getParticipantOrder(participantId) {
            // Find in portal members first
            const memberIndex = this.portalMembers.findIndex(m => m.email === participantId || m.id === participantId);
            if (memberIndex >= 0) {
                return memberIndex + 1;
            }
            
            // Fallback to participants
            const participantIndex = this.participants.findIndex(p => p.email === participantId || p.id === participantId);
            if (participantIndex >= 0) {
                return participantIndex + 1;
            }
            
            return '?';
        },
        getSelectedParticipantInfo() {
            if (!this.selectedParticipant || this.selectedParticipant === 'all') {
                return null;
            }
            
            // Find in portal members
            const member = this.portalMembers.find(m => m.email === this.selectedParticipant || m.id === this.selectedParticipant);
            if (member) {
                return member;
            }
            
            // Fallback to participants
            const participant = this.participants.find(p => p.email === this.selectedParticipant || p.id === this.selectedParticipant);
            return participant || null;
        },
        getFieldIcon(fieldType) {
            switch (fieldType) {
                case 'signature':
                    return 'mdi-draw';
                case 'text':
                    return 'mdi-form-textbox';
                case 'checkbox':
                    return 'mdi-checkbox-marked-outline';
                case 'radio':
                    return 'mdi-radiobox-marked';
                case 'choice':
                    return 'mdi-form-dropdown';
                default:
                    return 'mdi-form-textbox';
            }
        },
        async updateFieldAssignment(field, newParticipantEmail) {
            console.log('Updating field assignment:', field.fieldName, 'to', newParticipantEmail);
            
            // Find the new participant details
            const newParticipant = this.portalMembers.find(m => m.email === newParticipantEmail);
            if (!newParticipant) {
                console.error('Participant not found:', newParticipantEmail);
                return;
            }
            
            // Update the field in our local data
            field.participantId = newParticipant.email;
            field.participantEmail = newParticipant.email;
            field.participantName = newParticipant.name;
            field.participantColor = newParticipant.color;
            
            // Update in allFieldsData as well
            const fieldInAllData = this.allFieldsData.find(f => f.id === field.id);
            if (fieldInAllData) {
                fieldInAllData.participantId = newParticipant.email;
                fieldInAllData.participantEmail = newParticipant.email;
                fieldInAllData.participantName = newParticipant.name;
                fieldInAllData.participantColor = newParticipant.color;
            }
            
            // Update the annotation in the PDF viewer
            if (this.$refs.pdfViewer && this.$refs.pdfViewer.updateFieldParticipant) {
                await this.$refs.pdfViewer.updateFieldParticipant(field.id, newParticipant);
            }
            
            // Save the assignments to file metadata
            await this.saveFieldAssignments();
            
            // Re-apply participant filtering to update visibility
            if (this.selectedParticipant && this.$refs.pdfViewer && this.$refs.pdfViewer.setActiveParticipant) {
                this.$refs.pdfViewer.setActiveParticipant(this.selectedParticipant);
            }
            
            // Refresh the signature fields list to show the new color/assignment
            this.$forceUpdate();
            
            this.notification('success', 'Signature field assignment updated');
        },
        async saveFieldAssignments() {
            // Save field assignments to browser storage (persists with the file)
            // TODO: In the future, this could be saved to a database field on the File type
            if (!this.file || !this.vault) {
                console.warn('Cannot save assignments - file or vault not loaded');
                return;
            }
            
            try {
                // Extract just the assignment data we need to persist
                const assignments = this.signatureFieldsList.map(field => ({
                    fieldId: field.id,
                    fieldName: field.fieldName,
                    pageNumber: field.pageNumber,
                    participantEmail: field.participantEmail,
                    participantName: field.participantName,
                    participantColor: field.participantColor
                }));
                
                // Save to localStorage indexed by vault and file ID
                const storageKey = 'pdf_signature_assignments_' + this.vault.id + '_' + this.file.id;
                
                try {
                    localStorage.setItem(storageKey, JSON.stringify({
                        assignments: assignments,
                        vaultId: this.vault.id,
                        fileId: this.file.id,
                        fileName: this.file.name,
                        savedAt: new Date().toISOString()
                    }));
                    
                    console.log('Saved field assignments for file:', this.file.name, '-', assignments.length, 'signature fields');
                } catch (storageError) {
                    // Browser storage blocked (e.g., Safari tracking prevention)
                    console.warn('localStorage blocked by browser:', storageError.message);
                }
                
                // Also save overall progress to the backend
                await this.saveProgress();
                
            } catch (error) {
                console.error('Error saving field assignments:', error);
                this.notification('error', 'Failed to save field assignments');
            }
        },
        async saveProgress() {
             // Check if we have vault and file
             if (!this.vault || !this.file) return;

             // Calculate progress based on signature fields
             const fields = this.signatureFieldsList;
             const total = fields.length;
             // Ensure we check 'filled' property which is updated by onFieldStatusChanged
             const completed = fields.filter(f => f.filled).length;
             
             // Calculate per-participant progress
             const participantsStatus = {};
             fields.forEach(f => {
                 const pEmail = f.participantEmail || 'unassigned';
                 if (!participantsStatus[pEmail]) {
                     participantsStatus[pEmail] = { 
                        email: pEmail,
                        name: f.participantName || pEmail,
                        color: f.participantColor,
                        total: 0, 
                        completed: 0 
                     };
                 }
                 participantsStatus[pEmail].total++;
                 if (f.filled) {
                     participantsStatus[pEmail].completed++;
                 }
             });
             
             const progressData = {
                 total,
                 completed,
                 participants: Object.values(participantsStatus),
                 updatedAt: new Date().toISOString()
             };
             
             let status = 'in_progress';
             if (total > 0 && completed === total) {
                 status = 'completed';
             } else if (completed === 0) {
                 status = 'pending';
             } else if (total === 0) {
                 status = 'none';
             }
             
             try {
                 // In simulation mode, we do NOT want to update the backend status
                 // The "Sent" status should only be set by the real "Send for Signature" workflow
                 console.log('SIMULATION MODE: Would have saved signature progress:', status, progressData);
                 
                 /* 
                 await vaultService.mutation("updateFileSignatureStatus", {
                    file_id: this.file.id,
                    org_id: this.vault.org_id,
                    vault_id: this.vault.id,
                    signature_status: status,
                    signatures_outstanding: total - completed,
                    signature_progress: JSON.stringify(progressData)
                });
                */
             } catch (error) {
                 console.error('Error saving signature progress:', error);
             }
        },
        onFieldStatusChanged(statuses) {
            // Update field filled status based on event from WebViewer
            console.log('🎯 Controller received fieldStatusChanged event with', statuses.length, 'statuses');
            if (!this.requiredFieldsList) {
                console.log('⚠️ requiredFieldsList is not defined');
                return;
            }
            
            console.log('🎯 Current requiredFieldsList has', this.requiredFieldsList.length, 'fields');
            
            // IMPORTANT: Only update fields that are ALREADY in requiredFieldsList
            // Don't add new fields - the list has already been filtered for this participant/mode
            // Create a Set of valid field names for faster lookup
            const validFieldNames = new Set(this.requiredFieldsList.map(f => f.fieldName));
            
            statuses.forEach(status => {
                // Skip if this field is not in our filtered list
                if (!validFieldNames.has(status.fieldName)) {
                    console.log('🎯 Skipping status update for field not in filtered list:', status.fieldName);
                    return;
                }
                
                const field = this.requiredFieldsList.find(f => f.id === status.id || f.fieldName === status.fieldName);
                if (field) {
                    console.log('🎯 Updating field:', status.fieldName, 'from', field.filled, 'to', status.isFilled);
                    // Use Vue.set to ensure reactivity
                    this.$set(field, 'filled', status.isFilled);
                    console.log('🎯 After update, field.filled =', field.filled);
                }
            });
            
            console.log('🎯 Final requiredFieldsList state:');
            this.requiredFieldsList.forEach(f => {
                console.log('  -', f.fieldName, ':', f.filled ? '✅ FILLED' : '❌ EMPTY');
            });
            
            // Save progress whenever fields change (simulation mode only)
            if (this.isSimulationMode) {
                this.saveProgress();
            }
        },
        loadSavedAssignments() {
            // Load saved assignments from browser storage
            if (!this.file || !this.vault) {
                return null;
            }
            
            try {
                const storageKey = 'pdf_signature_assignments_' + this.vault.id + '_' + this.file.id;
                const savedData = localStorage.getItem(storageKey);
                
                if (savedData) {
                    const parsed = JSON.parse(savedData);
                    console.log('Loaded saved assignments from storage:', parsed.assignments.length, 'fields');
                    return parsed.assignments;
                }
            } catch (error) {
                // Browser storage blocked (e.g., Safari tracking prevention) or parse error
                if (error.name === 'SecurityError' || error.message.includes('storage')) {
                    console.warn('localStorage blocked by browser:', error.message);
                } else {
                    console.error('Error loading saved assignments:', error);
                }
            }
            
            return null;
        }
    }
});

